import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  addToCart,
  getCart,
  updateCart,
  removeCartItem,
  validateCart,
  updateCartItem,
  emptyCart,
  resetPickupAddress,
  createCartPromotion
} from 'catering/api/resources/cart';

export const CART_QUERY_KEY = 'cart';
export const CART_VALIDATION_QUERY_KEY = 'cart-validation';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60
};

function useCartQuery(customQueryOptions, queryParams = {}) {
  return useQuery(CART_QUERY_KEY, () => getCart(queryParams), {
    ...DEFAULT_QUERY_OPTIONS,
    ...customQueryOptions
  });
}

export function useCart(customQueryOptions, queryParams = {}) {
  return useCartQuery(customQueryOptions, queryParams);
}

export function useValidateCart(customQueryOptions) {
  const { data: cart } = useCart();
  return useQuery([CART_VALIDATION_QUERY_KEY, cart?.quantity], validateCart, {
    enabled: !!cart?.items?.length,
    staleTime: 0,
    cacheTime: 0,
    useErrorBoundary: false,
    ...customQueryOptions
  });
}

export function usePrefetchCart() {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    (async () => {
      await queryClient.prefetchQuery(CART_QUERY_KEY, getCart);
    })();
  }, [queryClient]);
}

export function useUpdateCart() {
  const queryClient = useQueryClient();
  return useMutation(updateData => updateCart(updateData), {
    onSuccess: updatedCart =>
      queryClient.setQueryData(CART_QUERY_KEY, updatedCart)
  });
}

export function useAddToCart() {
  const queryClient = useQueryClient();
  return useMutation(cartItem => addToCart(cartItem), {
    onSuccess: updatedCart =>
      queryClient.setQueryData(CART_QUERY_KEY, updatedCart)
  });
}

export function useEmptyCart() {
  const queryClient = useQueryClient();
  return useMutation(emptyCart, {
    onSuccess: emptyCart => queryClient.setQueryData(CART_QUERY_KEY, emptyCart)
  });
}

export function useRemoveCartItem() {
  const queryClient = useQueryClient();
  return useMutation(cartItem => removeCartItem(cartItem), {
    onSuccess: updatedCart =>
      queryClient.setQueryData(CART_QUERY_KEY, updatedCart)
  });
}

export function useUpdateCartItem() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ cartItemId, data }) => updateCartItem(cartItemId, data),
    {
      onSuccess: updatedCart =>
        queryClient.setQueryData(CART_QUERY_KEY, updatedCart)
    }
  );
}

export function useResetPickupAddress() {
  return useMutation(resetPickupAddress, {
    onSuccess: () => window.location.reload()
  });
}

export function useCreateCartPromotion() {
  return useMutation(createCartPromotion);
}

export * from 'catering/hooks/query/cart/selectors';
